import { useContext, useEffect, useState } from 'react'

import { Routes } from '@/configs'

import { useFileUploader, useInput, useRouting } from '@/hooks'

import { Button, Flex, Form, Input, Switch, Uploader, UploaderOutput } from '@/components'
import { HorizontalTagInput } from '@/components/core'
import { TagOption } from '@/components/core/HorizontalTagInput/HorizontalTagInput.hooks'
import { UploaderViewer } from '@/components/derived'
import { Layout } from '@/components/layout'

import { Alert, Api } from '@/modules'
import { CompanyResponse, UpdateCompanyRequest } from '@shared/models'
import { useParams } from 'react-router-dom'
import { CompanyFormContext, CompanyFormContextProvider, CreateCompanyType } from './FormCompany.context'
import { FormContainer, HeaderContainer, LogoContainer, TitleContainer } from './FormCompany.styles'

export function FormCompany() {
  return (
    <CompanyFormContextProvider>
      <Component />
    </CompanyFormContextProvider>
  )
}

function Component() {
  const { navigate, queryParams } = useRouting()
  const { isCreatingCompany, save } = useContext(CompanyFormContext)
  const { companyID } = useParams<{ companyID: string }>()
  const isEditMode = !!companyID

  const { sendAlert } = Alert.Hook()

  const [fetchCompany, , company] = Api.LazyQuery<CompanyResponse>(`companies/${companyID}`)

  const [systemLogoID, setSystemLogoID] = useState<string | null>(company?.systemLogoID ?? null)
  const [reportLogoID, setReportLogoID] = useState<string | null>(company?.reportLogoID ?? null)

  const [systemLogoChanged, setSystemLogoChanged] = useState(false)
  const [reportLogoChanged, setReportLogoChanged] = useState(false)

  const [fetchSystemLogo] = Api.PromiseQuery<string>(`files/:id`)
  const [fetchReportLogo] = Api.PromiseQuery<string>(`files/:id`)

  const { run: updateCompany } = Api.Command<UpdateCompanyRequest>(Api.Methods.PATCH, `companies/${companyID}`)

  const hasCompetitorsBinder = Switch.Hook()
  const governmentalBinder = Switch.Hook()
  const { value: name, binder: bindName, reset: resetName } = useInput()
  // const { value: keywords, binder: bindKeywords, reset: resetKeywords } = useInput()
  const { value: systemLogo, bind: bindSystemLogo, setValue: setSystemLogo } = useFileUploader()
  const { value: reportLogo, bind: bindReportLogo, setValue: setReportLogo } = useFileUploader()
  const { mae: mother } = queryParams

  const [programsAndInitiatives, setProgramsAndInitiatives] = useState<TagOption[]>([])
  const [advertisingCampaigns, setAdvertisingCampaigns] = useState<TagOption[]>([])
  const [slogans, setSlogans] = useState<TagOption[]>([])
  const [nicknamesAndCommonNames, setNicknamesAndCommonNames] = useState<TagOption[]>([])
  const [products, setProducts] = useState<TagOption[]>([])

  useEffect(() => {
    if (!companyID) return
    fetchCompany()
  }, [companyID])

  useEffect(() => {
    const loadLogo = async (id: string, setLogo: (input: UploaderOutput | undefined) => void) => {
      if (id === 'default-logo-color' || id === 'default-logo-gray') {
        return
      }

      if (id) {
        const [data] = await fetchSystemLogo({ id })
        if (data) {
          const [mimePart] = data.split(',')
          const mime = mimePart.match(/^data:(.*?);/)?.[1] || 'image/svg+xml'
          const extension = mime.split('/')[1]?.split('+')[0] || 'svg'
          const name = `logo.${extension}`
          setLogo({ mime, name, content: data })
        }
      }
    }
    if (systemLogoID) loadLogo(systemLogoID, setSystemLogo)
  }, [systemLogoID])

  useEffect(() => {
    const loadLogo = async (id: string, setLogo: (input: UploaderOutput | undefined) => void) => {
      if (id === 'default-logo-color' || id === 'default-logo-gray') {
        return
      }

      if (id) {
        const [data] = await fetchReportLogo({ id })
        if (data) {
          const [mimePart] = data.split(',')
          const mime = mimePart.match(/^data:(.*?);/)?.[1] || 'image/svg+xml'
          const extension = mime.split('/')[1]?.split('+')[0] || 'svg'
          const name = `logo.${extension}`
          setLogo({ mime, name, content: data })
        }
      }
    }
    if (reportLogoID) loadLogo(reportLogoID, setReportLogo)
  }, [reportLogoID])

  useEffect(() => {
    if (company) {
      resetName(company?.name)
      // resetKeywords(company?.keywords)
      if (company?.systemLogoID) setSystemLogoID(company?.systemLogoID)
      if (company?.reportLogoID) setReportLogoID(company?.reportLogoID)
      if (company?.hasCompetitors !== hasCompetitorsBinder.isChecked) hasCompetitorsBinder.toggle()
      if (company?.isGovernmental !== governmentalBinder.isChecked) governmentalBinder.toggle()

      if (company?.programsAndInitiatives) {
        const tags = company.programsAndInitiatives.split(';').filter((tag) => tag.trim() !== '')
        setProgramsAndInitiatives(tags.map((tag) => ({ id: tag.trim(), text: tag.trim() })))
      }
      if (company?.advertisingCampaigns) {
        const tags = company.advertisingCampaigns.split(';').filter((tag) => tag.trim() !== '')
        setAdvertisingCampaigns(tags.map((tag) => ({ id: tag.trim(), text: tag.trim() })))
      }
      if (company?.slogans) {
        const tags = company.slogans.split(';').filter((tag) => tag.trim() !== '')
        setSlogans(tags.map((tag) => ({ id: tag.trim(), text: tag.trim() })))
      }
      if (company?.nicknamesAndCommonNames) {
        const tags = company.nicknamesAndCommonNames.split(';').filter((tag) => tag.trim() !== '')
        setNicknamesAndCommonNames(tags.map((tag) => ({ id: tag.trim(), text: tag.trim() })))
      }
      if (company?.products) {
        const tags = company.products.split(';').filter((tag) => tag.trim() !== '')
        setProducts(tags.map((tag) => ({ id: tag.trim(), text: tag.trim() })))
      }
    }
  }, [company])

  const formTitle = isEditMode ? 'Editar Empresa' : 'Registrar Nova Empresa'

  const onSubmit = () => {
    const input = {} as CreateCompanyType

    if (systemLogoChanged && systemLogo) input.systemLogo = systemLogo
    if (reportLogoChanged && reportLogo) input.reportLogo = reportLogo

    if (isEditMode) {
      if (name !== company?.name) input.name = name
      // if (keywords !== company?.keywords) input.keywords = keywords
      if (hasCompetitorsBinder.isChecked !== company?.hasCompetitors)
        input.hasCompetitors = hasCompetitorsBinder.isChecked
      if (governmentalBinder.isChecked !== company?.isGovernmental) input.isGovernmental = governmentalBinder.isChecked

      const currentProgramsAndInitiatives = programsAndInitiatives
        .map((tag) => tag.text)
        .filter((value) => value && value.trim() !== '')
      const currentAdvertisingCampaigns = advertisingCampaigns
        .map((tag) => tag.text)
        .filter((value) => value && value.trim() !== '')
      const currentSlogans = slogans.map((tag) => tag.text).filter((value) => value && value.trim() !== '')
      const currentNicknamesAndCommonNames = nicknamesAndCommonNames
        .map((tag) => tag.text)
        .filter((value) => value && value.trim() !== '')
      const currentProducts = products.map((tag) => tag.text).filter((value) => value && value.trim() !== '')

      const originalProgramsAndInitiatives =
        company?.programsAndInitiatives ?
          company.programsAndInitiatives
            .split(';')
            .map((tag) => tag.trim())
            .filter((tag) => tag !== '')
        : []
      const originalAdvertisingCampaigns =
        company?.advertisingCampaigns ?
          company.advertisingCampaigns
            .split(';')
            .map((tag) => tag.trim())
            .filter((tag) => tag !== '')
        : []
      const originalSlogans =
        company?.slogans ?
          company.slogans
            .split(';')
            .map((tag) => tag.trim())
            .filter((tag) => tag !== '')
        : []
      const originalNicknamesAndCommonNames =
        company?.nicknamesAndCommonNames ?
          company.nicknamesAndCommonNames
            .split(';')
            .map((tag) => tag.trim())
            .filter((tag) => tag !== '')
        : []
      const originalProducts =
        company?.products ?
          company.products
            .split(';')
            .map((tag) => tag.trim())
            .filter((tag) => tag !== '')
        : []

      if (
        JSON.stringify(currentProgramsAndInitiatives.sort()) !== JSON.stringify(originalProgramsAndInitiatives.sort())
      ) {
        input.programsAndInitiatives = currentProgramsAndInitiatives
      }
      if (JSON.stringify(currentAdvertisingCampaigns.sort()) !== JSON.stringify(originalAdvertisingCampaigns.sort())) {
        input.advertisingCampaigns = currentAdvertisingCampaigns
      }
      if (JSON.stringify(currentSlogans.sort()) !== JSON.stringify(originalSlogans.sort())) {
        input.slogans = currentSlogans
      }
      if (
        JSON.stringify(currentNicknamesAndCommonNames.sort()) !== JSON.stringify(originalNicknamesAndCommonNames.sort())
      ) {
        input.nicknamesAndCommonNames = currentNicknamesAndCommonNames
      }
      if (JSON.stringify(currentProducts.sort()) !== JSON.stringify(originalProducts.sort())) {
        input.products = currentProducts
      }

      if (Object.keys(input).length === 0) {
        sendAlert({
          type: 'error',
          title: 'Erro ao atualizar empresa',
          message: 'Nenhum campo foi editado para atualização.',
        })
        return
      }
      updateCompany({
        input,
        onSuccess: () => navigate(Routes.CompanyDetails, companyID),
      })
      return
    }

    input.name = name
    // input.keywords = keywords
    input.hasCompetitors = hasCompetitorsBinder.isChecked
    input.isGovernmental = governmentalBinder.isChecked
    input.programsAndInitiatives = programsAndInitiatives
      .map((tag) => tag.text)
      .filter((value) => value && value.trim() !== '')
    input.advertisingCampaigns = advertisingCampaigns
      .map((tag) => tag.text)
      .filter((value) => value && value.trim() !== '')
    input.slogans = slogans.map((tag) => tag.text).filter((value) => value && value.trim() !== '')
    input.nicknamesAndCommonNames = nicknamesAndCommonNames
      .map((tag) => tag.text)
      .filter((value) => value && value.trim() !== '')
    input.products = products.map((tag) => tag.text).filter((value) => value && value.trim() !== '')

    if (mother) {
      delete input.hasCompetitors
      input.motherID = mother
    }
    save(input)
  }

  const handleCancel = () => {
    if (isEditMode && companyID) {
      navigate(Routes.CompanyDetails, companyID)
      return
    }
    navigate(Routes.Home)
  }

  return (
    <Layout.Root noMenu>
      <Layout.Padder>
        <FormContainer>
          <HeaderContainer>
            <TitleContainer>
              <h1>{formTitle}</h1>
              <span>
                {isEditMode ?
                  'Atualize os campos necessários para editar a empresa'
                : <>
                    Preencha todos os campos <strong>obrigatórios (*)</strong> para concluir o cadastro inicial da
                    empresa
                  </>
                }
              </span>
            </TitleContainer>
            <Button.Root onClick={handleCancel} $variant="secondary">
              Cancelar
            </Button.Root>
          </HeaderContainer>
          <Form title="Nova empresa" onSubmit={onSubmit}>
            <Flex $type="column" $align="stretch" $gap={24} $fit>
              <Input label="Nome da empresa" placeholder="Nome da empresa" {...bindName} required />
              <Flex $type="row" $justify="center" $align="center" $gap={24} $fit>
                <LogoContainer>
                  <Uploader
                    label="Logo em cores (.svg 417px/128px) - Opcional"
                    {...bindSystemLogo}
                    onUpload={(output) => {
                      bindSystemLogo.onUpload(output)
                      setSystemLogoChanged(true)
                    }}
                    requiredExtensions={['svg']}
                  />
                  <UploaderViewer value={systemLogo} />
                </LogoContainer>
                <LogoContainer>
                  <Uploader
                    label="Logo em cinza (.svg 417px/128px) - Opcional"
                    {...bindReportLogo}
                    onUpload={(output) => {
                      bindReportLogo.onUpload(output)
                      setReportLogoChanged(true)
                    }}
                    requiredExtensions={['svg']}
                  />
                  <UploaderViewer value={reportLogo} />
                </LogoContainer>
              </Flex>
            </Flex>
            {/* <div style={{ display: 'none' }}>
              <Input
                label="Palavras-chaves (separadas por vírgula)"
                placeholder="Palavras-chave"
                {...bindKeywords}
                //required
              />
            </div> */}

            <Flex $type="column" $align="stretch" $gap={20} $fit>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
                Configurações para Detecção de Citação no Título
              </h3>

              <HorizontalTagInput
                label="Programas e Iniciativas"
                placeholder="Digite programas e iniciativas separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={setProgramsAndInitiatives}
                preSelectedTags={programsAndInitiatives}
              />

              <HorizontalTagInput
                label="Campanhas Publicitárias"
                placeholder="Digite campanhas publicitárias separadas por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={setAdvertisingCampaigns}
                preSelectedTags={advertisingCampaigns}
              />

              <HorizontalTagInput
                label="Slogans"
                placeholder="Digite slogans separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={setSlogans}
                preSelectedTags={slogans}
              />

              <HorizontalTagInput
                label="Apelidos e Nomes Comuns"
                placeholder="Digite apelidos e nomes comuns separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={setNicknamesAndCommonNames}
                preSelectedTags={nicknamesAndCommonNames}
              />

              <HorizontalTagInput
                label="Produtos"
                placeholder="Digite produtos separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={setProducts}
                preSelectedTags={products}
              />
            </Flex>
            <Flex $type="row" $justify="space-between" $align="stretch" $gap={20} $fit>
              <Flex $type="row" $justify="flex-start" $align="stretch" $gap={20} $fit>
                {!mother && <Switch.Root label="Cadastrar concorrentes" {...hasCompetitorsBinder} />}
                <Switch.Root label="É instituição governamental" {...governmentalBinder} />
              </Flex>
              <Button.Root type="submit" $variant="primary" $loading={isCreatingCompany}>
                <Button.Text value={isEditMode ? 'Atualizar' : 'Salvar'} />
              </Button.Root>
            </Flex>
          </Form>
        </FormContainer>
      </Layout.Padder>
    </Layout.Root>
  )
}
